// 3-tier pricing comparison — designed so 6m + 12m visually dominate.
// Each card has its own data-testid="cp-plan-{id}" and CTA fires onBuyClick(packageId).
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { trackAddToCart } from "@/lib/pixel";

const TIER_HIGHLIGHTS = {
  care_plus_3m:  ["1 free wash", "1 free AC check", "1 wash @ ₹299", "₹500 off denting"],
  care_plus_6m:  ["2 free washes", "1 free AC check", "1 wheel alignment", "50% off labour"],
  care_plus_12m: ["3 free washes", "2 free AC checks", "100% off labour", "20+ redemptions"],
};

export default function CPPricing({ onBuyClick }) {
  const [pkgs, setPkgs] = useState(null);

  useEffect(() => {
    api.get("/customer/care-plus/packages")
      .then(({ data }) => setPkgs(data.packages))
      .catch(() => setPkgs([]));
  }, []);

  if (!pkgs || pkgs.length === 0) return null;

  return (
    <section
      id="pricing"
      data-testid="cp-pricing"
      className="bg-[#FAFAFA] text-[#0A0A0A] px-5 lg:px-8 py-16 sm:py-24"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Pick your plan</p>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            Three plans. <span className="text-[#D4AF37]">One smart choice.</span>
          </h2>
          <p className="mt-4 text-base text-[#52525B]">
            Pay-as-you-go pricing or commit to a year for the best value.
            Most car owners pick <strong className="text-[#0A0A0A]">6 months</strong> or <strong className="text-[#0A0A0A]">12 months</strong>.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-3 gap-4 md:gap-5 items-stretch">
          {pkgs.map((p, i) => {
            const isFeatured = p.popular || p.best_value;
            const savings = Math.max(0, p.worth_rupees - p.price_rupees);
            const monthly = Math.round(p.price_rupees / (p.validity_days / 30));
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative rounded-sm border ${
                  p.best_value
                    ? "bg-[#0A0A0A] text-white border-[#D4AF37] md:scale-[1.04] md:-mt-4 md:mb-0 shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                    : p.popular
                    ? "bg-white text-[#0A0A0A] border-[#0A0A0A]"
                    : "bg-white text-[#0A0A0A] border-[#E4E4E7] opacity-95"
                } p-6 sm:p-7 flex flex-col`}
                data-testid={`cp-plan-${p.id}`}
              >
                {p.best_value && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-extrabold uppercase tracking-[0.18em] px-3 py-1 rounded-sm flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Best Value
                  </div>
                )}
                {p.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#0A0A0A] text-[#D4AF37] text-[10px] font-extrabold uppercase tracking-[0.18em] px-3 py-1 rounded-sm flex items-center gap-1">
                    <Star className="w-3 h-3 fill-[#D4AF37]" /> Most Popular
                  </div>
                )}

                <div className="flex items-baseline justify-between">
                  <p className={`text-xs uppercase tracking-[0.22em] font-bold ${p.best_value ? "text-[#D4AF37]" : "text-[#71717A]"}`}>
                    {p.label}
                  </p>
                  <p className={`text-[10px] uppercase tracking-wider ${p.best_value ? "text-white/50" : "text-[#A1A1AA]"}`}>
                    {p.validity_days}-day validity
                  </p>
                </div>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-4xl sm:text-5xl font-extrabold">
                    ₹{p.price_rupees.toLocaleString("en-IN")}
                  </span>
                  <span className={`text-xs line-through ${p.best_value ? "text-white/40" : "text-[#A1A1AA]"}`}>
                    ₹{p.worth_rupees.toLocaleString("en-IN")}
                  </span>
                </div>
                <p className={`text-xs mt-1 ${p.best_value ? "text-white/60" : "text-[#71717A]"}`}>
                  ≈ ₹{monthly.toLocaleString("en-IN")}/month · save ₹{savings.toLocaleString("en-IN")}
                </p>

                <ul className={`mt-6 space-y-2.5 text-sm flex-1 ${p.best_value ? "text-white/85" : "text-[#27272A]"}`}>
                  <li className="flex items-start gap-2.5 font-semibold">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.best_value ? "text-[#D4AF37]" : "text-[#0A0A0A]"}`} />
                    {p.total_redemptions} total redemptions
                  </li>
                  {(TIER_HIGHLIGHTS[p.id] || []).map((h, k) => (
                    <li key={k} className="flex items-start gap-2.5">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.best_value ? "text-[#D4AF37]" : "text-[#71717A]"}`} />
                      {h}
                    </li>
                  ))}
                  <li className={`flex items-start gap-2.5 ${p.best_value ? "text-white/60" : "text-[#71717A]"}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.best_value ? "text-[#D4AF37]" : "text-[#71717A]"}`} />
                    Up to 2 cars · 100% genuine parts
                  </li>
                </ul>

                <Button
                  data-testid={`cp-plan-${p.id}-cta`}
                  onClick={() => { trackAddToCart({ source: "pricing_card", package_id: p.id, value: p.price_rupees }); onBuyClick(p.id); }}
                  className={`mt-7 w-full font-extrabold py-6 rounded-sm uppercase tracking-wider text-sm ${
                    p.best_value
                      ? "bg-[#D4AF37] hover:bg-[#E5C254] text-[#0A0A0A]"
                      : p.popular
                      ? "bg-[#0A0A0A] hover:bg-black text-white"
                      : "bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                  }`}
                >
                  {p.best_value ? "Get 12 Months →" : p.popular ? "Get 6 Months →" : "Start with 3 Months"}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-[#71717A]">
          All plans · Razorpay secured · Cancel anytime · No auto-renewal · 7-day refund
        </p>
      </div>
    </section>
  );
}
